import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import { CMSFeatureId, CMSFeatureTestConfig, hasResourceForTestTypeParams } from 'src/shared/types';
import globalStorageService from 'src/global/services/global-storage-service';
import cmsService from 'src/services/cms-service';
import useProject from './useProject';
import useProjectFilters from './useProjectFilters';

const useTestTypeFeatures = () => {
    const { isChegdProject, isGRIISProject } = useProject();
    const { currentProjectSelectedHabitatAssayType, currentProjectFilters } = useProjectFilters();
    const [featureTestConfig, setFeatureTestConfig] = useState<CMSFeatureTestConfig>(globalStorageService.getFeatureTestConfig() || {});

    useEffect(() => {
        if (featureTestConfig && !isEmpty(featureTestConfig)) {
            return;
        }

        const fetchFeatureTestConfig = async () => {
            const cmsFeatureTestConfig = await cmsService.getFeatureTestConfig();
            globalStorageService.setFeatureTestConfig(cmsFeatureTestConfig);
            setFeatureTestConfig(cmsFeatureTestConfig);
        };
        fetchFeatureTestConfig();
    }, []);

    const hasResourceForTestType = (params: hasResourceForTestTypeParams): boolean => {
        const { featureId, habitatId, assayId } = params;
        return featureTestConfig[featureId]?.includes(`${habitatId}-${assayId}`);
    };

    const hasMetricLevelFeature = (featureId: CMSFeatureId): boolean => {
        return hasResourceForTestType({
            featureId,
            habitatId: currentProjectSelectedHabitatAssayType?.habitat.habitatKey || '*',
            assayId: currentProjectSelectedHabitatAssayType?.assay.assayId || '*',
        });
    };

    const hasProjectLevelFeature = (featureId: CMSFeatureId) => {
        return (
            currentProjectFilters?.habitatAssays.some(entry => {
                return hasResourceForTestType({
                    featureId: featureId,
                    habitatId: entry.habitat.habitatKey,
                    assayId: entry.assay.assayId,
                });
            }) || false
        );
    };

    return {
        hasResourceForTestType,
        hasSpeciesRichnessFeature: hasMetricLevelFeature(CMSFeatureId.SPECIES_RICHNESS),
        hasEvolutionaryDiversityFeature: hasMetricLevelFeature(CMSFeatureId.EVOLUTIONARY_DIVERSITY),
        hasCommunityComparisonFeature: hasMetricLevelFeature(CMSFeatureId.COMMUNITY_COMPARISON),
        hasFishMetricsFeature: hasMetricLevelFeature(CMSFeatureId.FISH_METRICS),
        hasInvasivesFeature: hasMetricLevelFeature(CMSFeatureId.INVASIVES) && isGRIISProject,
        hasIucnRedListFeature: hasMetricLevelFeature(CMSFeatureId.IUCN_RED_LIST),
        hasFfdFeature: hasMetricLevelFeature(CMSFeatureId.FUNGAL_FUNCTIONAL_DIVERSITY),
        hasBfdFeature: hasMetricLevelFeature(CMSFeatureId.BACTERIAL_FUNCTIONAL_DIVERSITY),
        hasFbRatioFeature: hasMetricLevelFeature(CMSFeatureId.FUNGAL_TO_BACTERIAL_RATIO),
        hasSpeciesHighlightFeature: hasMetricLevelFeature(CMSFeatureId.SPECIES_HIGHLIGHT),
        hasMspiFeature: hasMetricLevelFeature(CMSFeatureId.MARINE_SEDIMENT_POLLUTION_INDEX),
        hasProjectLevelInvasivesFeature: hasProjectLevelFeature(CMSFeatureId.INVASIVES) && isGRIISProject,
        hasProjectLevelIucnRedlistFeature: hasProjectLevelFeature(CMSFeatureId.IUCN_RED_LIST),
        hasProjectLevelChegdFeature: hasProjectLevelFeature(CMSFeatureId.CHEGD) && isChegdProject,
        hasChegdFeature: hasMetricLevelFeature(CMSFeatureId.CHEGD) && isChegdProject,
        hasSpeciesGroupsFeature: hasMetricLevelFeature(CMSFeatureId.SPECIES_GROUPS),
        hasTotalSpeciesFeature: hasMetricLevelFeature(CMSFeatureId.TOTAL_SPECIES),
    };
};

export default useTestTypeFeatures;
